import classNames from 'classnames'
import React, { useEffect, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { ExtendableStyles, Testable } from 'utils-next/types'
import { Avatar, Button, Link as ExternalLink, Theme, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import ContentCopyIcon from 'mdi-react/ContentCopyIcon'
import ArrowForwardIcon from 'mdi-react/ArrowForwardIcon'

import { getUserName, User } from '../../../../../../../../entities-next/user'
import { Corporate, getApiCorporateHandlers } from '../../../../../../../../entities-next/corporate'
import { TemporalEntityId } from '../../../../../../../../entities-next/common'
import { getCorporateDetailsPath } from '../../../../../CorporateManager'
import { SUBSPACES_PATH } from '../../../../../SubspaceManager'

export type UserHeaderInfoProps = ExtendableStyles &
  Testable & {
    user: User
  }

export function UserHeaderInfo({ className, user, style, ...otherProps }: UserHeaderInfoProps) {
  const { classes } = useStyles()
  const name = getUserName(user) || 'New user'
  const { email } = user
  const { apiGetCorporate } = getApiCorporateHandlers()
  const [corporate, setCorporate] = useState<Corporate | null>(null)
  useEffect(() => {
    const retrieveCorporate = async () => {
      if (user.id !== TemporalEntityId.new) {
        setCorporate(await apiGetCorporate(user.corporateId))
      }
    }
    retrieveCorporate()
  }, [user])
  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    navigator.clipboard.writeText(user.id.toString())
    e.stopPropagation()
  }
  return (
    <div className={classNames(className, classes.container)} style={style} {...otherProps}>
      <Avatar className={classes.avatar} src={undefined} alt={`${name}'s profile picture`} />
      <div className={classes.userContent}>
        <Typography variant="h5">{name}</Typography>
        <div className={classes.extraInfo}>
          {corporate && (
            <Typography className={classes.extraInfoItem} variant="subtitle1">
              {corporate.name}
            </Typography>
          )}
          {email && (
            <Typography variant="subtitle1" className={classes.extraInfoItem}>
              <ExternalLink href={`mailTo:${email}`} target="_blank">
                {email}
              </ExternalLink>
            </Typography>
          )}
        </div>
      </div>
      <div className={classes.actionContainer}>
        <Button
          variant="contained"
          className={classes.buttonIcon}
          color="info"
          onClick={handleCopy}
          title="Copy user id"
          size="small"
        >
          <ContentCopyIcon size={16} />
          <span>Copy ID</span>
        </Button>
        {corporate && (
          <Link to={getCorporateDetailsPath(corporate?.id)} className={classes.noUnderline}>
            <Button variant="contained" size="small" color="info" className={classes.buttonIcon}>
              <ArrowForwardIcon size={16} />
              <span>Go to corporate</span>
            </Button>
          </Link>
        )}
        <Link to={`${SUBSPACES_PATH}?userId=${user.id}`} className={classes.noUnderline}>
          <Button variant="contained" size="small" color="info" className={classes.buttonIcon}>
            <ArrowForwardIcon size={16} />
            <span>Go to subspaces</span>
          </Button>
        </Link>
      </div>
    </div>
  )
}

const useStyles = makeStyles()(({ spacing, palette }: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: spacing(2),
  },
  avatar: {
    width: spacing(6),
    height: spacing(6),
  },
  userContent: {
    marginLeft: spacing(2),
    flex: '1 1 auto',
  },
  extraInfo: {
    display: 'flex',
    marginTop: spacing(0.5),
  },
  extraInfoItem: {
    position: 'relative',
    marginRight: spacing(2),
    ['&:after']: {
      content: '""',
      position: 'absolute',
      left: `calc(100% + ${spacing(2)}px)`,
      marginLeft: spacing(1),
      height: '100%',
      width: 1,
      backgroundColor: palette.grey[300],
    },
    ['&:last-child:after']: {
      display: 'none',
    },
  },
  actionContainer: {
    display: 'flex',
    gap: spacing(2),
  },
  buttonIcon: {
    display: 'flex',
    gap: spacing(1),
    textDecoration: 'none',
  },
  noUnderline: {
    textDecoration: 'none',
  },
}))
